import React, { createContext, useCallback, useContext, useState } from "react";
import { PrintSettingsStateTypes } from "../types/print_settings";
import { Product } from "../types/product";
import {
  PrintLabelLayout,
  PrintPriceDisplayOptions,
  syncPrintLabelLayout,
} from "../utils/printLabelLayout";

interface PrintLabelLayoutContextValue {
  layouts: Record<string, PrintLabelLayout>;
  syncLayout: (
    key: string,
    data: Product,
    settings: PrintSettingsStateTypes,
    options: PrintPriceDisplayOptions,
  ) => void;
  updateLayout: (
    key: string,
    update: (layout: PrintLabelLayout) => PrintLabelLayout,
  ) => void;
}

const PrintLabelLayoutContext = createContext<PrintLabelLayoutContextValue | null>(null);

export const PrintLabelLayoutProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [layouts, setLayouts] = useState<Record<string, PrintLabelLayout>>({});

  const syncLayout = useCallback(
    (
      key: string,
      data: Product,
      settings: PrintSettingsStateTypes,
      options: PrintPriceDisplayOptions,
    ) => {
      setLayouts((previous) => ({
        ...previous,
        [key]: syncPrintLabelLayout(previous[key], data, settings, options),
      }));
    },
    [],
  );

  const updateLayout = useCallback(
    (key: string, update: (layout: PrintLabelLayout) => PrintLabelLayout) => {
      setLayouts((previous) => {
        const current = previous[key];
        if (!current) return previous;
        return { ...previous, [key]: update(current) };
      });
    },
    [],
  );

  return (
    <PrintLabelLayoutContext.Provider
      value={{ layouts, syncLayout, updateLayout }}
    >
      {children}
    </PrintLabelLayoutContext.Provider>
  );
};

export const usePrintLabelLayouts = () => {
  const context = useContext(PrintLabelLayoutContext);
  if (!context) {
    throw new Error(
      "usePrintLabelLayouts must be used inside PrintLabelLayoutProvider",
    );
  }
  return context;
};
